/**
 * 線形回帰分析
 */

import { KeisukeAdapterError } from '../keisukeAdapterI18n';

/**
 * 線形回帰結果
 */
export interface LinearRegressionResult {
  slope: number;        // 傾き（b）
  intercept: number;    // 切片（a）
  rSquared: number;     // R²値
  n: number;           // データポイント数
  standardError?: number;  // 標準誤差
  slopeSE?: number;     // 傾きの標準誤差
  interceptSE?: number; // 切片の標準誤差
}

/**
 * 標準回帰（y = ax + b）
 */
export function linearRegression(
  xData: number[],
  yData: number[]
): LinearRegressionResult {
  if (xData.length !== yData.length || xData.length < 2) {
    throw new KeisukeAdapterError('insufficientDataPoints');
  }

  const n = xData.length;

  // 平均値の計算
  const xMean = xData.reduce((sum, x) => sum + x, 0) / n;
  const yMean = yData.reduce((sum, y) => sum + y, 0) / n;

  // 傾きの計算
  let numerator = 0;
  let denominator = 0;

  for (let i = 0; i < n; i++) {
    const xDiff = xData[i] - xMean;
    const yDiff = yData[i] - yMean;
    numerator += xDiff * yDiff;
    denominator += xDiff * xDiff;
  }

  if (Math.abs(denominator) < 1e-10) {
    throw new KeisukeAdapterError('zeroVarianceX');
  }

  const slope = numerator / denominator;

  // 切片の計算
  const intercept = yMean - slope * xMean;

  // R²値の計算
  let ssr = 0;  // 回帰平方和
  let sst = 0;  // 総平方和

  for (let i = 0; i < n; i++) {
    const yPredicted = slope * xData[i] + intercept;
    const yDiff = yData[i] - yMean;
    ssr += (yPredicted - yMean) * (yPredicted - yMean);
    sst += yDiff * yDiff;
  }

  const rSquared = Math.abs(sst) < 1e-10 ? 0 : ssr / sst;

  return {
    slope,
    intercept,
    rSquared,
    n,
  };
}

/**
 * 原点を通る回帰（y = ax）
 */
export function linearRegressionThroughOrigin(
  xData: number[],
  yData: number[]
): LinearRegressionResult {
  if (xData.length !== yData.length || xData.length < 2) {
    throw new KeisukeAdapterError('insufficientDataPoints');
  }

  const n = xData.length;

  // 傾きの計算（原点を通るため、a = Σ(xy) / Σ(x²)）
  let numerator = 0;
  let denominator = 0;

  for (let i = 0; i < n; i++) {
    numerator += xData[i] * yData[i];
    denominator += xData[i] * xData[i];
  }

  if (Math.abs(denominator) < 1e-10) {
    throw new KeisukeAdapterError('zeroVarianceX');
  }

  const slope = numerator / denominator;
  const intercept = 0;

  // R²（原点通過）: 1 - SSE / SST0、SST0 = Σ y_i²（切片なし回帰の慣例。平均中心化の SST は不適切）
  let sse = 0;
  let sst0 = 0;
  for (let i = 0; i < n; i++) {
    const yPredicted = slope * xData[i];
    sse += (yData[i] - yPredicted) ** 2;
    sst0 += yData[i] * yData[i];
  }
  const rSquared =
    sst0 < 1e-10 ? 0 : Math.max(0, Math.min(1, 1 - sse / sst0));

  return {
    slope,
    intercept,
    rSquared,
    n,
  };
}

/**
 * Y値を予測
 */
export function predictY(
  x: number,
  regression: LinearRegressionResult
): number {
  return regression.slope * x + regression.intercept;
}

/**
 * 検量線: 吸光度（Y）から濃度（X）を逆算する。
 * @param regressionType `origin` のとき y = ax、それ以外は y = ax + b
 */
export function estimateConcentrationFromAbsorbance(
  absorbance: number,
  regression: LinearRegressionResult,
  regressionType: 'standard' | 'origin' = 'standard'
): number | null {
  if (!Number.isFinite(absorbance) || !Number.isFinite(regression.slope) || regression.slope === 0) {
    return null;
  }
  if (regressionType === 'origin') {
    return absorbance / regression.slope;
  }
  return (absorbance - regression.intercept) / regression.slope;
}

